import { create } from 'zustand';

import { environment } from '@env/environment';
import { parseFlagValues, type FlagValues } from '@core/access/flags';

/**
 * The shipped feature-flag layers: the environment file, and the deployment
 * file the host serves.
 *
 * What a flag is and what it switches off lives in `@core/access/flags`.
 * Overrides per scope are organisation settings, edited on Settings > Feature
 * flags and held in the settings store; this store is only the baseline they
 * override, plus any `scopes` block the deployment file carries - which is
 * how a scoped switch reaches everybody before the Settings API exists:
 * export it from the Settings page and drop it on the host.
 *
 * Screens do not read this store. They ask `useCan` or `useFlag`, which
 * resolve the user's scope over these layers.
 */

interface FeatureFlagConfig {
  values?: unknown;
}

/* `environment` is typed from environment.ts alone; read the block defensively. */
const config = (environment as { featureFlags?: FeatureFlagConfig }).featureFlags;

export const ENVIRONMENT_FLAG_VALUES: FlagValues = parseFlagValues(config?.values, 'environment file');

/** Where `/feature-flags.json` came from, for the Settings page to report. */
export type DeploymentFlagStatus = 'pending' | 'loaded' | 'absent' | 'failed';

interface FeatureFlagState {
  environment: FlagValues;
  deployment: FlagValues;
  /** The deployment file's per-scope overrides, keyed by scope id. */
  deploymentScopes: Record<string, FlagValues>;
  deploymentStatus: DeploymentFlagStatus;
  setDeployment: (deployment: FlagValues, scopes: Record<string, FlagValues>, status: DeploymentFlagStatus) => void;
}

export const useFeatureFlagStore = create<FeatureFlagState>((set) => ({
  environment: ENVIRONMENT_FLAG_VALUES,
  deployment: {},
  deploymentScopes: {},
  deploymentStatus: 'pending',

  setDeployment: (deployment, deploymentScopes, deploymentStatus) =>
    set({ deployment, deploymentScopes, deploymentStatus })
}));

/* ------------------------------------------------------------ the file -- */

const DEPLOYMENT_FLAGS_TIMEOUT_MS = 2500;

function parseScopes(input: unknown): Record<string, FlagValues> {
  if (!input || typeof input !== 'object' || Array.isArray(input)) {
    return {};
  }

  const scopes: Record<string, FlagValues> = {};

  for (const [id, values] of Object.entries(input as Record<string, unknown>)) {
    scopes[id === 'global' ? id : id.toUpperCase()] = parseFlagValues(values, `feature-flags.json scope ${id}`);
  }

  return scopes;
}

/**
 * Reads `/feature-flags.json` from the host this bundle was served from.
 *
 * Awaited in main.tsx before the first render, so a feature switched off on
 * the server never flashes on screen. It fails open to the environment file:
 * a missing, slow or malformed file costs a console warning and the shipped
 * values, never a blank portal.
 *
 * The file is `{ "flags": { "<flag id>": true | false }, "scopes": { "SG":
 * { ... } } }`; anything else in it is ignored, so it can carry a `$comment`.
 */
export async function loadDeploymentFlags(): Promise<void> {
  const { setDeployment } = useFeatureFlagStore.getState();
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), DEPLOYMENT_FLAGS_TIMEOUT_MS);

  try {
    const response = await fetch(`${import.meta.env.BASE_URL}feature-flags.json`, {
      cache: 'no-store',
      signal: controller.signal
    });

    /*
     * The SPA rewrite answers an unknown path with index.html and a 200, so a
     * host without the file returns HTML here rather than a 404.
     */
    if (!response.ok || !(response.headers.get('content-type') ?? '').includes('json')) {
      setDeployment({}, {}, 'absent');
      return;
    }

    const body = (await response.json()) as { flags?: unknown; scopes?: unknown };
    setDeployment(parseFlagValues(body?.flags, 'feature-flags.json'), parseScopes(body?.scopes), 'loaded');
  } catch (error) {
    console.warn('[feature-flags] feature-flags.json could not be read; using the environment file.', error);
    setDeployment({}, {}, 'failed');
  } finally {
    clearTimeout(timer);
  }
}
